import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SignIn, SignOut, Coffee, Play, ArrowLeft, CheckCircle, User, Translate, Clock, X } from "@phosphor-icons/react";

// -------- i18n --------
const STRINGS = {
  en: {
    header_kicker: "// US OPERATIONS",
    header_title: "Shop Floor Kiosk",
    change_worker: "Change worker",
    loading: "Loading…",
    picker_title: "Tap your name",
    picker_hint: "Select yourself to punch in, punch out, or update production.",
    picker_search: "Search by name or code…",
    picker_none: "No matching worker",
    signed_in_as: "Signed in as",
    assignment: "Your assignment today",
    operation: "Operation",
    operations: "Operations",
    shift: "Shift",
    no_shift: "No shift scheduled for you today. You can still punch in — ask supervisor to assign your work.",
    time_tracking: "Time tracking",
    punch_in: "Punch IN",
    punch_out: "Punch OUT",
    break_start: "Break Start",
    break_end: "Break End",
    hourly_production: "Hourly Production",
    total: "Total",
    save_production: "Save Production",
    saving: "Saving…",
    done: "Done — next worker",
    hourly_locked: "Hourly production entry unlocks once your shift start/end is set on the schedule.",
    loading_day: "Loading your day…",
    toast_in: "Punched IN",
    toast_out: "Punched OUT",
    toast_break_start: "Break started",
    toast_break_end: "Break ended",
    toast_at: "at",
    toast_save_ok: "Saved · Total",
    toast_save_fail: "Save failed",
    toast_generic_fail: "Failed. Try again.",
    toast_no_load: "Could not load your info. Ask supervisor.",
    toast_no_staff: "Could not load staff list. Ask supervisor.",
    my_hours_btn: "View My Hours",
    my_hours_title: "My Hours · Last 4 Weeks",
    week_of: "Week of",
    weekly_total: "Weekly total",
    grand_total: "Grand total",
    no_hours: "No hours logged.",
    today_tag: "TODAY",
    close: "Close",
    hours_load_fail: "Could not load hours.",
    col_day: "Day",
    col_in: "In",
    col_out: "Out",
    col_break: "Break",
    col_hrs: "Hrs",
  },
  es: {
    header_kicker: "// US OPERATIONS",
    header_title: "Kiosco de Taller",
    change_worker: "Cambiar trabajador",
    loading: "Cargando…",
    picker_title: "Toca tu nombre",
    picker_hint: "Selecciónate para marcar entrada, salida o actualizar producción.",
    picker_search: "Buscar por nombre o código…",
    picker_none: "Ningún trabajador coincide",
    signed_in_as: "Trabajador",
    assignment: "Tu asignación de hoy",
    operation: "Operación",
    operations: "Operaciones",
    shift: "Turno",
    no_shift: "Hoy no tienes turno programado. Aún puedes marcar entrada — pídele al supervisor que te asigne trabajo.",
    time_tracking: "Control de tiempo",
    punch_in: "Marcar ENTRADA",
    punch_out: "Marcar SALIDA",
    break_start: "Inicio Descanso",
    break_end: "Fin Descanso",
    hourly_production: "Producción por Hora",
    total: "Total",
    save_production: "Guardar Producción",
    saving: "Guardando…",
    done: "Listo — siguiente trabajador",
    hourly_locked: "La entrada de producción por hora se activa cuando el supervisor define tu horario.",
    loading_day: "Cargando tu día…",
    toast_in: "ENTRADA marcada",
    toast_out: "SALIDA marcada",
    toast_break_start: "Descanso iniciado",
    toast_break_end: "Descanso terminado",
    toast_at: "a las",
    toast_save_ok: "Guardado · Total",
    toast_save_fail: "No se pudo guardar",
    toast_generic_fail: "Falló. Inténtalo de nuevo.",
    toast_no_load: "No se pudo cargar tu información. Pregunta al supervisor.",
    toast_no_staff: "No se pudo cargar la lista. Pregunta al supervisor.",
    my_hours_btn: "Ver Mis Horas",
    my_hours_title: "Mis Horas · Últimas 4 Semanas",
    week_of: "Semana del",
    weekly_total: "Total semanal",
    grand_total: "Total general",
    no_hours: "Sin horas registradas.",
    today_tag: "HOY",
    close: "Cerrar",
    hours_load_fail: "No se pudieron cargar las horas.",
    col_day: "Día",
    col_in: "Entrada",
    col_out: "Salida",
    col_break: "Descanso",
    col_hrs: "Hrs",
  },
};

const LANG_STORAGE_KEY = "kiosk_lang";

function useKioskLang() {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem(LANG_STORAGE_KEY) || "en"; } catch { return "en"; }
  });
  const t = useMemo(() => {
    const dict = STRINGS[lang] || STRINGS.en;
    return (k) => dict[k] ?? STRINGS.en[k] ?? k;
  }, [lang]);
  const setAndPersist = (l) => {
    setLang(l);
    try { localStorage.setItem(LANG_STORAGE_KEY, l); } catch { /* ignore */ }
  };
  return { lang, setLang: setAndPersist, t };
}

// Public endpoints — do NOT go through the authenticated `api` client (which sends cookies
// that would 401 for anonymous users on some setups). Use a bare axios instance instead.
const publicApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  timeout: 15000,
});

const hoursBetween = (start, end) => {
  const toM = (v) => { const [h, m] = (v || "").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
  const s = toM(start); const e = toM(end);
  if (!s || !e || e <= s) return [];
  const out = [];
  for (let m = Math.floor(s / 60) * 60; m + 60 <= Math.ceil(e / 60) * 60; m += 60) {
    const hh = Math.floor(m / 60);
    out.push(`${String(hh).padStart(2, "0")}:00`);
  }
  return out;
};
const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const prettyToday = (lang) => new Date().toLocaleDateString(lang === "es" ? "es-US" : "en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

export default function Kiosk() {
  const { lang, setLang, t } = useKioskLang();
  const [staff, setStaff] = useState([]);
  const [picked, setPicked] = useState(null); // {id, name, designation, code}
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await publicApi.get("/public/staff");
        setStaff(r.data);
      } catch (e) {
        toast.error(t("toast_no_staff"));
      } finally { setLoading(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900" data-testid="kiosk-page">
      <div className="border-b-4 border-slate-900 bg-white">
        <div className="max-w-5xl mx-auto flex items-center justify-between p-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <img src="/logo-gate.jpg" alt="GATE" className="w-14 h-14 border-2 border-slate-900 object-cover" />
            <div>
              <div className="font-mono-ui text-xs text-blue-700 tracking-widest">{t("header_kicker")}</div>
              <div className="font-display text-3xl uppercase tracking-tighter leading-none">{t("header_title")}</div>
              <div className="text-xs text-slate-600 mt-1">{prettyToday(lang)}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LangToggle lang={lang} setLang={setLang} />
            {picked && (
              <button
                onClick={() => setPicked(null)}
                className="brut-btn brut-btn-dark px-4 py-3 inline-flex items-center gap-2 text-lg"
                data-testid="kiosk-back-btn"
              >
                <ArrowLeft size={18} weight="bold" /> {t("change_worker")}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6">
        {loading ? (
          <div className="text-center py-20 font-display uppercase text-2xl">{t("loading")}</div>
        ) : !picked ? (
          <StaffPicker staff={staff} onPick={setPicked} t={t} />
        ) : (
          <KioskPanel staff={picked} onDone={() => setPicked(null)} t={t} />
        )}
      </div>
    </div>
  );
}

function LangToggle({ lang, setLang }) {
  return (
    <div className="inline-flex border-2 border-slate-900 overflow-hidden" data-testid="kiosk-lang-toggle">
      {[["en", "EN"], ["es", "ES"]].map(([code, label]) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          className={`px-3 py-2 font-display uppercase text-sm inline-flex items-center gap-1 ${lang === code ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-100"}`}
          data-testid={`kiosk-lang-${code}`}
        >
          {code === "en" && <Translate size={14} weight="bold" />}
          {label}
        </button>
      ))}
    </div>
  );
}

function StaffPicker({ staff, onPick, t }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return staff;
    return staff.filter((s) => s.name.toLowerCase().includes(term) || (s.code || "").toLowerCase().includes(term));
  }, [staff, q]);

  return (
    <div>
      <h2 className="font-display text-3xl uppercase tracking-tight mb-1">{t("picker_title")}</h2>
      <p className="text-sm text-slate-600 mb-4">{t("picker_hint")}</p>

      <input
        type="search"
        placeholder={t("picker_search")}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="w-full text-lg px-4 py-3 border-2 border-slate-900 bg-white mb-4"
        data-testid="kiosk-staff-search"
        autoFocus
      />

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500 font-display uppercase">{t("picker_none")}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="kiosk-staff-grid">
          {filtered.map((s) => (
            <button
              key={s.id}
              onClick={() => onPick(s)}
              className="border-2 border-slate-900 bg-white hover:bg-blue-50 active:bg-blue-100 p-4 text-left transition-colors"
              data-testid={`kiosk-pick-${s.name}`}
            >
              <div className="flex items-start gap-2">
                <User size={20} weight="bold" className="text-blue-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="font-display uppercase text-lg leading-tight break-words">{s.name}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{s.designation}{s.code ? ` · ${s.code}` : ""}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function KioskPanel({ staff, onDone, t }) {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(null);
  const [showHours, setShowHours] = useState(false);

  const load = async () => {
    try {
      const r = await publicApi.get(`/public/kiosk-state?staff_id=${staff.id}`);
      setState(r.data);
    } catch (e) {
      toast.error(t("toast_no_load"));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [staff.id]);

  const punch = async (action) => {
    setSaving(action);
    try {
      const r = await publicApi.post("/public/punch", { staff_id: staff.id, action, time: nowHHMM() });
      setState((prev) => ({ ...prev, timesheet: r.data }));
      const labelKey = { in: "toast_in", out: "toast_out", break_start: "toast_break_start", break_end: "toast_break_end" }[action];
      toast.success(`${t(labelKey)} ${t("toast_at")} ${nowHHMM()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("toast_generic_fail"));
    } finally { setSaving(null); }
  };

  if (!state) return <div className="text-center py-16 font-display uppercase">{t("loading_day")}</div>;

  const { schedule, timesheet, date } = state;
  const shiftStart = schedule?.shift_start;
  const shiftEnd = schedule?.shift_end;
  const ops = (schedule?.operations && schedule.operations.length > 0)
    ? schedule.operations
    : (schedule?.operation_code ? [{ id: schedule.operation_id, code: schedule.operation_code, name: schedule.operation_name }] : []);

  return (
    <div className="space-y-5">
      <div className="border-2 border-slate-900 bg-white p-5">
        <div className="text-xs font-mono-ui text-slate-500 uppercase tracking-widest">{t("signed_in_as")}</div>
        <div className="font-display text-4xl uppercase tracking-tight leading-none mt-1">{staff.name}</div>
        <div className="text-sm text-slate-600 mt-1">{staff.designation}{staff.code ? ` · ${staff.code}` : ""}</div>
        <div className="text-xs font-mono-ui text-slate-600 mt-2">{date}</div>
      </div>

      {schedule ? (
        <div className="border-2 border-slate-900 bg-blue-50 p-5">
          <div className="text-xs font-mono-ui text-blue-800 uppercase tracking-widest">{t("assignment")}</div>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            <div>
              <div className="text-xs text-slate-600">{ops.length > 1 ? t("operations") : t("operation")}</div>
              {ops.map((op, i) => (
                <div key={i} className="mt-0.5">
                  <div className="font-display text-2xl leading-tight">{op.code}</div>
                  <div className="text-sm">{op.name}</div>
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs text-slate-600">{t("shift")}</div>
              <div className="font-display text-2xl leading-tight">{shiftStart} – {shiftEnd}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="border-2 border-amber-500 bg-amber-50 p-4 font-mono-ui text-sm">
          {t("no_shift")}
        </div>
      )}

      <div>
        <div className="text-xs font-mono-ui text-slate-500 uppercase tracking-widest mb-2">{t("time_tracking")}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <PunchButton icon={SignIn} label={t("punch_in")} value={timesheet?.in_time} color="emerald" onClick={() => punch("in")} disabled={saving} testid="punch-in" />
          <PunchButton icon={Coffee} label={t("break_start")} value={timesheet?.break_start} color="amber" onClick={() => punch("break_start")} disabled={saving} testid="punch-break-start" />
          <PunchButton icon={Play} label={t("break_end")} value={timesheet?.break_end} color="amber" onClick={() => punch("break_end")} disabled={saving} testid="punch-break-end" />
          <PunchButton icon={SignOut} label={t("punch_out")} value={timesheet?.out_time} color="rose" onClick={() => punch("out")} disabled={saving} testid="punch-out" />
        </div>
      </div>

      {ops.length > 0 ? (
        <DailyTotalsGrid staff={staff} ops={ops} existing={timesheet?.daily_totals || []} onSaved={(newTs) => setState((p) => ({ ...p, timesheet: newTs }))} t={t} />
      ) : (
        <div className="text-xs text-slate-500 italic">{t("hourly_locked")}</div>
      )}

      <div className="text-center pt-4 flex items-center justify-center gap-3 flex-wrap">
        <button
          onClick={() => setShowHours(true)}
          className="brut-btn px-5 py-3 inline-flex items-center gap-2 text-lg bg-white border-2 border-slate-900 hover:bg-slate-50"
          data-testid="kiosk-my-hours-btn"
        >
          <Clock size={18} weight="bold" /> {t("my_hours_btn")}
        </button>
        <button
          onClick={onDone}
          className="brut-btn brut-btn-dark px-5 py-3 inline-flex items-center gap-2 text-lg"
          data-testid="kiosk-done-btn"
        >
          <CheckCircle size={18} weight="bold" /> {t("done")}
        </button>
      </div>

      {showHours && <MyHoursModal staff={staff} t={t} onClose={() => setShowHours(false)} />}
    </div>
  );
}

function PunchButton({ icon: Icon, label, value, color, onClick, disabled, testid }) {
  const colorMap = {
    emerald: { bg: "bg-emerald-500", hover: "hover:bg-emerald-600", tag: "bg-emerald-100 text-emerald-800 border-emerald-600" },
    amber:   { bg: "bg-amber-500",   hover: "hover:bg-amber-600",   tag: "bg-amber-100 text-amber-800 border-amber-600" },
    rose:    { bg: "bg-rose-500",    hover: "hover:bg-rose-600",    tag: "bg-rose-100 text-rose-800 border-rose-600" },
  };
  const c = colorMap[color] || colorMap.emerald;
  const done = !!value;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`border-2 border-slate-900 p-4 text-left transition-colors ${done ? c.tag : `${c.bg} ${c.hover} text-white`} disabled:opacity-60`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <Icon size={22} weight="bold" />
        <div className="font-display uppercase text-lg leading-tight">{label}</div>
      </div>
      <div className="font-mono-ui text-2xl mt-2">{value || "— : —"}</div>
    </button>
  );
}

function DailyTotalsGrid({ staff, ops, existing, onSaved, t }) {
  const initial = useMemo(() => {
    const m = {}; for (const op of ops) m[op.id] = "";
    for (const e of existing) if (m[e.operation_id] !== undefined) m[e.operation_id] = e.qty ?? "";
    return m;
  }, [ops, existing]);
  const [qtyMap, setQtyMap] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setQtyMap(initial); }, [initial]);

  const total = Object.values(qtyMap).reduce((a, b) => a + (parseFloat(b) || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      const r = await publicApi.post("/public/production", {
        staff_id: staff.id,
        daily_totals: ops.map((op) => ({ operation_id: op.id, qty: parseFloat(qtyMap[op.id]) || 0 })),
      });
      onSaved(r.data);
      toast.success(`${t("toast_save_ok")} ${total}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("toast_save_fail"));
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-mono-ui text-slate-500 uppercase tracking-widest">{t("hourly_production")}</div>
        <div className="text-sm font-mono-ui">{t("total")}: <b className="font-display text-2xl">{total}</b></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {ops.map((op) => (
          <label key={op.id} className="border-2 border-slate-900 bg-white p-4 block" data-testid={`dt-${op.code}`}>
            <div className="font-display text-2xl leading-tight">{op.code}</div>
            <div className="text-sm text-slate-600 mb-2">{op.name}</div>
            <input
              type="number"
              inputMode="numeric"
              min="0"
              value={qtyMap[op.id]}
              onChange={(e) => setQtyMap({ ...qtyMap, [op.id]: e.target.value })}
              className="w-full text-5xl font-mono-ui font-bold bg-white outline-none text-right"
              placeholder="0"
              data-testid={`dt-input-${op.code}`}
            />
          </label>
        ))}
      </div>
      <div className="mt-3 text-right">
        <button
          onClick={save}
          disabled={saving}
          className="brut-btn brut-btn-primary px-5 py-3 text-lg disabled:opacity-60"
          data-testid="kiosk-save-production"
        >
          {saving ? t("saving") : t("save_production")}
        </button>
      </div>
    </div>
  );
}


function MyHoursModal({ staff, t, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await publicApi.get(`/public/staff-hours?staff_id=${staff.id}&weeks=4`);
        setData(r.data);
      } catch {
        setErr(true);
      }
    })();
  }, [staff.id]);

  const fmtHrs = (min) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h}:${String(m).padStart(2, "0")}`;
  };
  const fmtDate = (iso) => {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="my-hours-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative border-2 border-slate-900 bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b-2 border-slate-900 px-5 py-3 bg-slate-900 text-white">
          <div className="min-w-0">
            <div className="text-[10px] font-mono-ui uppercase tracking-widest opacity-70">{staff.name}</div>
            <div className="font-display uppercase text-lg leading-tight">{t("my_hours_title")}</div>
          </div>
          <button onClick={onClose} className="text-white p-1" data-testid="my-hours-close">
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {err && <div className="text-sm text-rose-700">{t("hours_load_fail")}</div>}
          {!err && !data && <div className="font-mono-ui text-sm">{t("loading")}</div>}
          {data && (
            <>
              {data.weeks.slice().reverse().map((w) => {
                const anyData = w.days.some((d) => d.in_time || d.out_time || d.worked_minutes > 0);
                return (
                  <div key={w.week_start} className="border-2 border-slate-900">
                    <div className="bg-slate-100 px-3 py-2 flex items-center justify-between flex-wrap gap-2 border-b-2 border-slate-900">
                      <div className="font-display uppercase text-sm">
                        {t("week_of")} {fmtDate(w.week_start)} — {fmtDate(w.week_end)}
                      </div>
                      <div className="font-mono-ui text-sm">
                        <span className="text-slate-500">{t("weekly_total")}: </span>
                        <span className="font-bold text-base" data-testid={`my-hours-week-${w.week_start}-total`}>{fmtHrs(w.worked_minutes)}</span>
                      </div>
                    </div>
                    {!anyData ? (
                      <div className="px-3 py-4 text-sm text-slate-500 italic">{t("no_hours")}</div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-300">
                            <th className="text-left px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-widest">{t("col_day")}</th>
                            <th className="text-left px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-widest">{t("col_in")}</th>
                            <th className="text-left px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-widest">{t("col_out")}</th>
                            <th className="text-left px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-widest">{t("col_break")}</th>
                            <th className="text-right px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-widest">{t("col_hrs")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {w.days.map((d) => (
                            <tr key={d.date} className={`border-b border-slate-200 ${d.is_today ? "bg-amber-50" : ""}`} data-testid={`my-hours-day-${d.date}`}>
                              <td className="px-3 py-1.5">
                                <span className="font-display uppercase mr-2">{d.weekday}</span>
                                <span className="font-mono-ui text-xs text-slate-600">{fmtDate(d.date)}</span>
                                {d.is_today && <span className="ml-2 text-[10px] font-mono-ui bg-amber-500 text-white px-1.5 py-0.5">{t("today_tag")}</span>}
                              </td>
                              <td className="px-3 py-1.5 font-mono-ui">{d.in_time || "—"}</td>
                              <td className="px-3 py-1.5 font-mono-ui">{d.out_time || "—"}</td>
                              <td className="px-3 py-1.5 font-mono-ui text-xs text-slate-600">
                                {d.break_start && d.break_end ? `${d.break_start}–${d.break_end}` : "—"}
                              </td>
                              <td className="px-3 py-1.5 text-right font-mono-ui font-bold">
                                {d.worked_minutes > 0 ? fmtHrs(d.worked_minutes) : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}

              <div className="border-2 border-slate-900 bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
                <div className="font-display uppercase">{t("grand_total")}</div>
                <div className="font-mono-ui text-2xl font-bold" data-testid="my-hours-grand-total">{fmtHrs(data.grand_worked_minutes)}</div>
              </div>
            </>
          )}
          <div className="text-right">
            <button onClick={onClose} className="brut-btn brut-btn-dark px-5 py-2.5" data-testid="my-hours-close-bottom">{t("close")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
